import React from 'react'
import parse, { HTMLReactParserOptions, Element, domToReact } from 'html-react-parser'
import { ResponsiveEmbedWrapper } from 'components/EmbedPreview'
import linkifyHtml from 'linkify-html'
import { SOCIAL_EMBED_HOSTS } from 'constants/media'

type LinkifyHtmlOptions = Parameters<typeof linkifyHtml>[1]

const LINKIFY_OPTIONS: LinkifyHtmlOptions = {
  defaultProtocol: 'https',
  target: {
    url: '_blank'
  },
  rel: 'noreferrer noopener'
}

function cleanAttributes(attribs: Record<string, string>): Record<string, any> {
  const cleaned = { ...attribs }
  if (cleaned.style && typeof cleaned.style === 'string') {
    delete cleaned.style
  }
  return cleaned
}

function preprocessHtml(html: string): string {
  return html.replace(/\s*style\s*=\s*["'][^"']*["']/gi, '')
}

export function parseHtmlWithEmbeds(html: string): React.ReactNode {
  const preprocessedHtml = preprocessHtml(html)
  const linkifiedHtml = linkifyHtml(preprocessedHtml, LINKIFY_OPTIONS)

  const options: HTMLReactParserOptions = {
    replace: domNode => {
      if (domNode instanceof Element) {
        if (domNode.attribs?.style && typeof domNode.attribs.style === 'string') {
          domNode.attribs = cleanAttributes(domNode.attribs)
        }

        // Force embedded images to fit their container (never overflow the modal
        // width). Drop any width/height attributes so the constraint always wins.
        if (domNode.name === 'img') {
          const attribs = domNode.attribs || {}
          return (
            <img
              key={`img-${attribs.src ?? attribs.alt ?? 'embed'}`}
              src={attribs.src}
              alt={attribs.alt ?? ''}
              srcSet={attribs.srcset}
              style={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
            />
          )
        }

        if (domNode.name === 'a' && domNode.attribs?.href) {
          const href = domNode.attribs.href
          if (isSupportedEmbedUrl(href)) {
            return <ResponsiveEmbedWrapper key={`embed-${href}`} url={href} />
          }

          return (
            <a
              key={`link-${href}`}
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              {...domNode.attribs}
            >
              {domToReact(domNode.children, options)}
            </a>
          )
        }
      }
      
      return undefined
    }
  }

  return parse(linkifiedHtml, options)
}


function isSupportedEmbedUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase().replace('www.', '')
    const allSupportedHosts = [
      ...SOCIAL_EMBED_HOSTS.YOUTUBE,
      ...SOCIAL_EMBED_HOSTS.TIKTOK,
      ...SOCIAL_EMBED_HOSTS.INSTAGRAM,
      ...SOCIAL_EMBED_HOSTS.FACEBOOK,
      ...SOCIAL_EMBED_HOSTS.TWITTER
    ]
    return allSupportedHosts.some(supportedHost => host === supportedHost || host.endsWith(`.${supportedHost}`))
  } catch {
    return false
  }
}
